import React, {useEffect, useState} from 'react';
import API from "libs/api";
import { Dialog, Modal, Button, Tabs, TabList, Tab, TabPanel } from 'react-aria-components';
import PROCESSING from "components/cmp_processing/cmp_processing";
import TAB_PROFILE from './tab_profile';
import TAB_AGES from './tab_ages';
import TAB_ACTIVITIES from './tab_activities';
import TAB_LOCATIONS from './tab_locations';
import TAB_OTHER from './tab_other';

import ICON from 'components/cmp_icon/cmp_icon';

import './mdl_domain.css';

const getItemIdentity = (item, type) => {
  if (!item || typeof item !== 'object') return '';
  const value = type === 'activity'
    ? item.activity_id || item.id || item.activity_name || item.name || item.activity
    : item.analysis_location || item.address || item.address1 || item.street ||
    item.location || item.name;
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
};

const getApprovedItemCount = (items, matches, type) => {
  const mergedItems = new Map();
  [...(Array.isArray(matches) ? matches : []), ...(Array.isArray(items) ? items : [])]
    .forEach((item, index) => {
    const identity = getItemIdentity(item, type) || `${type}-${index}`;
    const current = mergedItems.get(identity);
    mergedItems.set(identity, {
       ...current,
       ...item,
       is_extracted: current?.is_extracted === true || item?.is_extracted === true
    });
    });

  return [...mergedItems.values()].filter((item) => {
    const isAccepted = typeof item.review_status === 'string'
    ? item.review_status.toUpperCase() === 'ACCEPTED'
    : item.is_extracted === true;
    const isVerifiedLocation = (item.latitude ?? item.lat) != null &&
    (item.longitude ?? item.lon) != null &&
    item.geocodeConfidence != null;
    return isAccepted && (type !== 'location' || isVerifiedLocation);
  }).length;
};


export default function MDL_DOMAIN({onClose, onOpen, onRefresh, var_domainHost, domainId}) {

  //  variable declarations ------------------------------------------------------------------------------------------

  const [domain, setDomain] = useState({});
  const [processing, setProcessing] = useState('inactive');
  const [selectedTab, setSelectedTab] = useState('profile');
  const [var_can_process, set_can_process] = useState(true);
  const [providerData, setProviderData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisProcessing, setAnalysisProcessing] = useState('inactive');
  const [analysisError, setAnalysisError] = useState(false);
  const [activityCatalog, setActivityCatalog] = useState([]);
  const [activityCatalogLoaded, setActivityCatalogLoaded] = useState(false);
  const [actionError, setActionError] = useState(null);
  const approvedActivityCount = getApprovedItemCount(
    providerData?.extractions?.activities,
    analysis?.activity_matches,
    'activity'
  );
  const approvedLocationCount = getApprovedItemCount(
    providerData?.extractions?.locations,
    analysis?.location_matches,
    'location'
  );



  //  event listeners ------------------------------------------------------------------------------------------------

  useEffect(() => {
    if (onOpen) {
      setSelectedTab('profile');
      setDomain({});
      setProviderData({});
      setAnalysis(null);
      setAnalysisProcessing('inactive');
      setAnalysisError(false);
      setActionError(null);
      setProcessing('inactive');
    }
  }, [onOpen])

  useEffect(() => {
    if (domainId && onOpen) {
      getDomain(domainId);
      getDomainAnalysis(domainId);
      if (!activityCatalogLoaded) getActivityCatalog();
    }
  }, [domainId, onOpen]);



  //  async functions ------------------------------------------------------------------------------------------------

  async function getDomain(domainId) {
    setProcessing('processing');
    try {
      const result = await API_get_domain(domainId);
      setDomain(result.provider_json || {});
      setProviderData(result.provider_json || {});
      setProcessing('inactive');
    } catch (e) {
      console.error('Error fetching domain:', e);
      setProcessing('error');
    }
  }

  async function getDomainAnalysis(domainId) {
    setAnalysisProcessing('processing');
    setAnalysisError(false);
    try {
      const result = await API_get_domain_analysis(domainId);
      setAnalysis(result);
      setAnalysisProcessing('inactive');
    } catch (e) {
      console.error('Error fetching domain analysis:', e);
      setAnalysis(null);
      setAnalysisError(true);
      setAnalysisProcessing('error');
    }
  }

  async function getActivityCatalog() {
    try {
      const result = await API_get_activity_catalog();
      setActivityCatalog(result.results || []);
      setActivityCatalogLoaded(true);
    } catch (e) {
      console.error('Error fetching activity catalog:', e);
      setActivityCatalog([]);
    }
  }

  //  API calls ------------------------------------------------------------------------------------------------------

  function API_get_domain(domainId) {
    return API.get('admin-domains', '/get-domain/' + domainId);
  }

  function API_get_domain_analysis(domainId) {
    return API.get('admin-domains', '/get-domain-analysis/' + domainId);
  }

  function API_get_activity_catalog() {
    return API.get('admin-activities', '/get-activities', {
      queryStringParameters: {
        limit: 10000,
        offset: 0,
        search: '',
        sortby: 'activity',
        sortorder: 'asc'
      }
    });
  }

  function API_put_domain(body) {
    return API.put('admin-domains', '/put-domain', { body });
  }



  //  event functions ------------------------------------------------------------------------------------------------

// Update functions for each tab
  const updateProviderInfo = (updates) => {
    setProviderData(prev => ({
      ...prev,
      extractions: {
        ...prev.extractions,
        provider: { ...prev.extractions?.provider, ...updates }
      }
    }));
  };

  const updateHost = (host) => {
    setProviderData(prev => ({
      ...prev,
      host
    }));
  };

  const updateActivities = (activities) => {
    setProviderData(prev => ({
      ...prev,
      extractions: { ...prev.extractions, activities }
    }));
  };

  const updateAges = (ages) => {
    setProviderData(prev => ({
      ...prev,
      extractions: { ...prev.extractions, ages }
    }));
  };

  const updateLocations = (locations) => {
    setProviderData(prev => ({
      ...prev,
      extractions: { ...prev.extractions, locations }
    }));
  };

  async function onClick_save() {
    setProcessing('processing');
    setActionError(null);
    try {
      await API_put_domain({ id: domainId, action: 'SAVE', provider_json: providerData });
      onRefresh?.();
      onClose();
    } catch (e) {
      console.error('Save failed:', e);
      setActionError('Failed to save changes. Please try again.');
      setProcessing('inactive');
    }
  }

  async function onClick_not_provider() {
    setProcessing('processing');
    setActionError(null);
    try {
      await API_put_domain({ id: domainId, action: 'NOT_PROVIDER', provider_json: providerData });
      onRefresh?.();
      onClose();
    } catch (e) {
      console.error('Not-a-provider update failed:', e);
      setActionError('Failed to mark as not a provider. Please try again.');
      setProcessing('inactive');
    }
  }

  async function onClick_publish() {
    if (approvedActivityCount === 0 || approvedLocationCount === 0) {
      setActionError('Publish requires at least one accepted activity and one accepted, verified location.');
      return;
    }
    setProcessing('processing');
    setActionError(null);
    try {
      await API_put_domain({ id: domainId, action: 'PUBLISH', provider_json: providerData });
      onRefresh?.();
      onClose();
    } catch (e) {
      console.error('Publish failed:', e);
      setActionError('Failed to publish. Please try again.');
      setProcessing('inactive');
    }
  }

  // RENDER APP ======================================================================================================

  return (
    <Modal id="mdl_domain" isDismissable isOpen={onOpen} onOpenChange={onClose}>
      <Dialog aria-labelledby="domain-title">
        <div className="header">
          <div className='header__inner'>
            <h2 id="domain-title" className='text--lg bold'>{domain?.extractions?.provider?.name}</h2>
          </div>
        </div>


        <div className='content'>
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={setSelectedTab}
            className='tab-set'
          >
            <TabList className='tab-list'>
              <Tab id="profile">Profile</Tab>
              <Tab id="ages">Ages</Tab>
              <Tab id="locations">Locations ({approvedLocationCount})</Tab>
              <Tab id="activities">Activities ({approvedActivityCount})</Tab>
              <Tab id="other">Other</Tab>
            </TabList>

            <TabPanel id="profile"><TAB_PROFILE providerData={providerData} updateProvider={updateProviderInfo} updateHost={updateHost} onOpen={onOpen} /></TabPanel>
            <TabPanel id="ages"><TAB_AGES providerData={providerData} updateAges={updateAges} /></TabPanel>
            <TabPanel id="locations"><TAB_LOCATIONS analysis={analysis} loading={analysisProcessing === 'processing'} error={analysisError} providerData={providerData} updateLocations={updateLocations} /></TabPanel>
            <TabPanel id="activities"><TAB_ACTIVITIES analysis={analysis} activityCatalog={activityCatalog} loading={analysisProcessing === 'processing'} error={analysisError} providerData={providerData} updateActivities={updateActivities} /></TabPanel>
            <TabPanel id="other"><TAB_OTHER domain={providerData} /></TabPanel>
          </Tabs>
        </div>

        <PROCESSING state={processing}/>
        <div className='footer'>
          {actionError && (
            <div className='footer__error text--sm' role='alert'>{actionError}</div>
          )}
          <div className='footer__actions'>
            <Button onPress={onClose} className='btn btn--secondary' isDisabled={processing === 'processing'}>
              Cancel
            </Button>
            <Button onPress={onClick_save} className='btn btn--secondary' isDisabled={processing === 'processing'}>
              Save
            </Button>
            <Button onPress={onClick_not_provider} className='btn btn--secondary' isDisabled={processing === 'processing'}>
              Not a provider
            </Button>
            <Button onPress={onClick_publish} className='btn btn--primary' isDisabled={processing === 'processing'}>
              Publish
            </Button>
          </div>
        </div>
      </Dialog>
    </Modal>
  )
}